import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { Event } from '../types';

const EventEditPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user, userRole } = useAuth();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
    status: 'draft' as 'draft' | 'public'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadEvent = React.useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await apiClient.get(`/v1/events/${eventId}`);
      const eventData = response.data.data;
      setEvent(eventData);

      // Populate form
      setFormData({
        name: eventData.name,
        start_date: eventData.start_date,
        end_date: eventData.end_date,
        location: eventData.location,
        description: eventData.description,
        status: eventData.status
      });
    } catch (err: any) {
      console.error('Failed to load event:', err);
      setLoadError(err.response?.data?.error?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Event name is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate < startDate) {
        newErrors.end_date = 'End date cannot be before start date';
      }
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      await apiClient.put(`/v1/events/${eventId}`, formData);
      navigate(`/events/${eventId}`);
    } catch (err: any) {
      console.error('Failed to update event:', err);
      setSubmitError(
        err.response?.data?.error?.message || 'Failed to update event. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiClient.delete(`/v1/events/${eventId}`);
      navigate('/events');
    } catch (err: any) {
      console.error('Failed to delete event:', err);
      alert(err.response?.data?.error?.message || 'Failed to delete event. Please try again.');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Check authorization
  const isCreator = event && user && event.creator_id === user.sub;
  const isCoOrganizer = event && user && event.co_organizers?.includes(user.sub);
  const canEdit = isCreator || isCoOrganizer;
  const canDelete = isCreator || userRole === 'admin';

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading event...</p>
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {loadError || 'Event not found'}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/events')}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Events
        </button>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          You don't have permission to edit this event.
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/events/${eventId}`)}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Event
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-lg-8 offset-lg-2">
          <button
            className="btn btn-link text-decoration-none mb-3"
            onClick={() => navigate(`/events/${eventId}`)}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Event
          </button>

          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">
                <i className="fas fa-edit me-2"></i>
                Edit Event
              </h4>
            </div>

            <div className="card-body">
              {submitError && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Event Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={submitting}
                    placeholder="e.g., TechConf 2024"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="start_date" className="form-label">
                      Start Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
                      id="start_date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                    {errors.start_date && (
                      <div className="invalid-feedback">{errors.start_date}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="end_date" className="form-label">
                      End Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.end_date ? 'is-invalid' : ''}`}
                      id="end_date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                    {errors.end_date && (
                      <div className="invalid-feedback">{errors.end_date}</div>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="location" className="form-label">
                    Location <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    disabled={submitting}
                    placeholder="e.g., London, UK"
                  />
                  {errors.location && (
                    <div className="invalid-feedback">{errors.location}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    id="description"
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    disabled={submitting}
                    placeholder="Describe your event..."
                  ></textarea>
                  {errors.description && (
                    <div className="invalid-feedback">{errors.description}</div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="status"
                      name="status"
                      checked={formData.status === 'public'}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked ? 'public' : 'draft' }))}
                      disabled={submitting}
                    />
                    <label className="form-check-label" htmlFor="status">
                      <strong>Public Event</strong>
                    </label>
                    <div className="form-text">
                      Public events are visible to all users. Draft events are only visible to you and co-organizers.
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <div>
                    {canDelete && (
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={submitting}
                      >
                        <i className="fas fa-trash me-2"></i>
                        Delete Event
                      </button>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => navigate(`/events/${eventId}`)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Event Info */}
          <div className="card mt-3">
            <div className="card-body">
              <h6 className="text-muted mb-2">Event Information</h6>
              <div className="row">
                <div className="col-md-6">
                  <small className="text-muted">Created:</small>
                  <div>{new Date(event.created_at).toLocaleString()}</div>
                </div>
                <div className="col-md-6">
                  <small className="text-muted">Last Updated:</small>
                  <div>{new Date(event.updated_at).toLocaleString()}</div>
                </div>
              </div>
              {event.co_organizers && event.co_organizers.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">Co-Organizers:</small>
                  <div>{event.co_organizers.length} co-organizer{event.co_organizers.length !== 1 ? 's' : ''}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirm Delete
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete <strong>{event.name}</strong>?
                </p>
                <p className="text-danger mb-0">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  This action cannot be undone. All sessions and data associated with this event will be permanently deleted.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Delete Event
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventEditPage;
